import React from 'react';
import {
  Box,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from '../TokenAwareTextField';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import {
  StepQuestion,
  StepQuestionType,
  slugifyKey,
  uniqueKey,
} from '../../../../../support/campaigns';
import { GhostButton } from '../primitives';

/**
 * Author the lightweight question set for a `question` step. Edits the array persisted at
 * `metadata.questions` (the bare-array form `stepQuestions()` tolerates) — the trimmed alternative
 * to a full Assessment. Pure controlled component: it owns no state, just maps `value`/`onChange`.
 *
 * Each question carries a stable `key` (auto-derived from the label, kept unique within the set) so
 * the SE's answers on their enrollment survive relabelling. Choice types expose a one-per-line
 * options editor; every other type needs only a label.
 */

const QUESTION_TYPE_OPTIONS: { value: StepQuestionType; label: string }[] = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'scale', label: 'Scale (1–5)' },
  { value: 'single_choice', label: 'Single choice' },
  { value: 'multi_choice', label: 'Multiple choice' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
];

const CHOICE_TYPES = new Set<StepQuestionType>(['single_choice', 'multi_choice']);

const optionsToText = (q: StepQuestion): string =>
  (q.options || []).map((o) => (o.label && o.label !== o.value ? `${o.value}|${o.label}` : o.value)).join('\n');

/** Parse the one-per-line options editor. `value|Label` splits a distinct value from its label. */
const textToOptions = (text: string): StepQuestion['options'] =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [value, ...rest] = line.split('|');
      const v = value.trim();
      const label = rest.join('|').trim();
      return { value: v, label: label || v };
    });

export function QuestionSetEditor(props: {
  value: StepQuestion[];
  onChange: (questions: StepQuestion[]) => void;
  disabled?: boolean;
}): JSX.Element {
  const { value, onChange, disabled } = props;
  const items = value || [];

  const patch = (index: number, next: Partial<StepQuestion>) => {
    onChange(items.map((q, i) => (i === index ? { ...q, ...next } : q)));
  };

  const add = () => {
    const key = uniqueKey('question', items.map((q) => q.key));
    onChange([...items, { key, label: '', type: 'text', required: false }]);
  };

  const remove = (index: number) => onChange(items.filter((_, i) => i !== index));

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  // Keep the key in sync with the label while it is still auto-derivable (untouched questions),
  // but never clobber an answer-bearing key once the label has diverged — only refresh a key that
  // still matches its label's slug or the placeholder.
  const relabel = (index: number, label: string) => {
    const q = items[index];
    const derivable =
      !q.label || q.key === slugifyKey(q.label) || /^question(-\d+)?$/.test(q.key);
    const next: Partial<StepQuestion> = { label };
    if (derivable) {
      next.key = uniqueKey(label || 'question', items.filter((_, i) => i !== index).map((x) => x.key));
    }
    patch(index, next);
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        Questions the SE answers in the runner. Answers are saved on their enrollment — a lightweight
        alternative to a full Assessment.
      </Typography>

      {items.length === 0 && (
        <Typography variant="body2" color="text.disabled">
          No questions yet.
        </Typography>
      )}

      {items.map((q, index) => (
        <Box
          key={index}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 18 }}>
                {index + 1}.
              </Typography>
              <TextField
                value={q.label}
                onChange={(e) => relabel(index, e.target.value)}
                label="Question"
                size="small"
                fullWidth
                disabled={disabled}
              />
              <Tooltip title="Move up">
                <span>
                  <IconButton size="small" onClick={() => move(index, -1)} disabled={disabled || index === 0}>
                    <ArrowUpwardOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => move(index, 1)}
                    disabled={disabled || index === items.length - 1}
                  >
                    <ArrowDownwardOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove">
                <span>
                  <IconButton size="small" onClick={() => remove(index)} disabled={disabled}>
                    <DeleteOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                select
                value={q.type}
                onChange={(e) => patch(index, { type: e.target.value as StepQuestionType })}
                label="Type"
                size="small"
                sx={{ minWidth: 160 }}
                disabled={disabled}
              >
                {QUESTION_TYPE_OPTIONS.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                value={q.help ?? ''}
                onChange={(e) => patch(index, { help: e.target.value || undefined })}
                label="Help text (optional)"
                size="small"
                fullWidth
                disabled={disabled}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                <Switch
                  checked={!!q.required}
                  onChange={(e) => patch(index, { required: e.target.checked })}
                  disabled={disabled}
                />
                <Typography variant="body2">Required</Typography>
              </Box>
            </Stack>

            {CHOICE_TYPES.has(q.type) && (
              <TextField
                value={optionsToText(q)}
                onChange={(e) => patch(index, { options: textToOptions(e.target.value) })}
                label="Options (one per line)"
                helperText="One choice per line. Use value|Label to store a distinct value."
                size="small"
                multiline
                minRows={2}
                fullWidth
                disabled={disabled}
              />
            )}
          </Stack>
        </Box>
      ))}

      <Box>
        <GhostButton onClick={add} disabled={disabled}>
          <AddOutlinedIcon fontSize="small" style={{ marginRight: 4 }} />
          Add question
        </GhostButton>
      </Box>
    </Stack>
  );
}

export default QuestionSetEditor;
